import type {
  TrainingEvalSuccessTermsView,
  TrainingImageMetadataTaskView,
} from "@/api/trainingEval";

const SUCCESS_METRICS = [
  ["object_success_rate", "Object Success Rate", "object_success"],
  ["robot_success_rate", "Robot Success Rate", "robot_success"],
  ["task_success_rate", "Task Success Rate", "task_success"],
] as const;

type IsaacConditionCounts = { satisfied: number; measured: number };
export const ISAAC_FINAL_NOTE =
  "최종 성공은 TASK 성공 조건을 충족한 시점에 Policy가 완료(done)를 선언한 경우입니다.";

function finiteMetric(metrics: Readonly<Record<string, number>>, key: string): number | null {
  const value = metrics[key];
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

// 반올림한 비율에서 건수를 역산하지 않는다. 집계와 원본이 다르면 분수를 붙이지 않는다.
export function matchingIsaacCounts(
  value: number | null,
  satisfied: number,
  measured: number,
): IsaacConditionCounts | null {
  return value !== null &&
    Number.isSafeInteger(satisfied) &&
    Number.isSafeInteger(measured) &&
    satisfied >= 0 &&
    measured > 0 &&
    satisfied <= measured &&
    Math.abs(satisfied / measured - value) < 0.000001
    ? { satisfied, measured }
    : null;
}

export function isaacSuccessRates(
  metrics: Readonly<Record<string, number>>,
  terms?: TrainingEvalSuccessTermsView,
) {
  const episodes = terms?.episodes ?? [];
  return SUCCESS_METRICS.map(([key, label, term]) => {
    const rawValue = finiteMetric(metrics, key);
    const value = rawValue !== null && rawValue >= 0 && rawValue <= 1 ? rawValue : null;
    const points = terms?.terms.find((candidate) => candidate.name === term)?.values ?? [];
    // Episode index로 연결한다. term의 values 순서는 Episode 목록과 다를 수 있다.
    const valuesByEpisode = new Map(
      points.map((point) => [point.episode_array_index, point.value]),
    );
    let measured = 0;
    let satisfied = 0;
    for (const episode of episodes) {
      const recordedValue = valuesByEpisode.get(episode.index);
      if (recordedValue !== 0 && recordedValue !== 1) continue;
      measured += 1;
      satisfied += recordedValue;
    }
    const counts = matchingIsaacCounts(value, satisfied, measured);
    return {
      key,
      label,
      term,
      value,
      counts,
      countsMismatch: value !== null && measured > 0 && counts === null,
    };
  });
}

export type IsaacStage = { name: string; value: number | null };

export function isaacStages(
  task: TrainingImageMetadataTaskView | null | undefined,
  metrics: Readonly<Record<string, number>>,
): IsaacStage[] {
  const rows = (task?.progress_stages_order ?? []).map((name) => ({
    name,
    value: finiteMetric(metrics, `stage_reached_rates.reached_stage_${name}`),
  }));
  return rows.some((row) => row.value !== null) ? rows : [];
}
